import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (dateKey) => {
  if (!dateKey) return "";
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const getRangeLabel = (start, end, label) => {
  if (start && end) {
    if (start === end) return formatDateLabel(start);
    return `${formatDateLabel(start)} - ${formatDateLabel(end)}`;
  }

  if (start) return `From ${formatDateLabel(start)}`;
  if (end) return `Until ${formatDateLabel(end)}`;
  return label;
};

export default function DateRangeFilter({
  start = "",
  end = "",
  onStartChange,
  onEndChange,
  onClear,
  label = "Date range",
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleToday = () => {
    const today = toDateKey(new Date());
    onStartChange?.(today);
    onEndChange?.(today);
    setAnchorEl(null);
  };

  const handleThisWeek = () => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - today.getDay());
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    onStartChange?.(toDateKey(startDate));
    onEndChange?.(toDateKey(endDate));
    setAnchorEl(null);
  };

  const handleClear = () => {
    onClear?.();
    setAnchorEl(null);
  };

  const handleStartChange = (nextStart) => {
    onStartChange?.(nextStart);

    if (nextStart && end && end < nextStart) {
      onEndChange?.(nextStart);
    }
  };

  const handleEndChange = (nextEnd) => {
    if (start && nextEnd && nextEnd < start) {
      onEndChange?.(start);
      return;
    }

    onEndChange?.(nextEnd);
  };

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: 34,
          minWidth: 160,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          size="small"
          variant="text"
          startIcon={<CalendarMonthOutlinedIcon fontSize="small" />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            flex: 1,
            minHeight: 34,
            justifyContent: "flex-start",
            px: 1.2,
            textTransform: "none",
            borderRadius: 0,
            fontSize: 12,
            color: start || end ? "text.primary" : "text.secondary",
            "& .MuiButton-startIcon": { mr: 0.7 },
          }}
        >
          {getRangeLabel(start, end, label)}
        </Button>
        {start || end ? (
          <Tooltip title="Clear range">
            <IconButton size="small" onClick={handleClear} sx={{ mr: 0.4 }}>
              <ClearOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            width: 300,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 14px 36px rgba(12, 30, 37, 0.18)",
          },
        }}
      >
        <Stack spacing={1.25}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Select date range
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pick start and end dates for this table.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Start"
              type="date"
              value={start}
              onChange={(event) => handleStartChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              label="End"
              type="date"
              value={end}
              onChange={(event) => handleEndChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: start || undefined }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ justifyContent: "space-between" }}
          >
            <Button size="small" variant="text" onClick={handleClear}>
              Clear
            </Button>
            <Stack direction="row" spacing={0.75}>
              <Button size="small" variant="outlined" onClick={handleToday}>
                Today
              </Button>
              <Button size="small" variant="contained" onClick={handleThisWeek}>
                This week
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
}
